import type { BridgeInbound, BridgeOutbound } from '../types';

const INBOUND = new Set<BridgeInbound['type']>(['host:pause', 'host:resume', 'host:setBalance', 'host:getState', 'host:restore', 'host:setTheme', 'host:setBrand']);

/** postMessage bridge between the game (inside an iframe) and the host page. */
export class Bridge {
  private readonly allowed: string[];
  private readonly onMessage: (m: BridgeInbound) => void;
  private readonly handler = (e: MessageEvent) => this.receive(e);
  private readonly embedded = typeof window !== 'undefined' && window.parent !== window;

  constructor(o: { allowedOrigins: string[]; onMessage: (m: BridgeInbound) => void }) {
    this.allowed = o.allowedOrigins;
    this.onMessage = o.onMessage;
    if (this.embedded) window.addEventListener('message', this.handler);
  }

  private originAllowed(origin: string): boolean {
    return this.allowed.includes('*') || this.allowed.includes(origin);
  }

  private receive(e: MessageEvent): void {
    if (!this.originAllowed(e.origin)) return;
    const data = e.data as Partial<BridgeInbound> | null;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
    if (!INBOUND.has(data.type)) return;
    if (data.type === 'host:setBalance' && typeof (data as { balance?: unknown }).balance !== 'number') return;
    if (data.type === 'host:setTheme' && typeof (data as { theme?: unknown }).theme !== 'string') return;
    if (data.type === 'host:setBrand') {
      const b = (data as { brand?: unknown }).brand;
      if (b !== null && typeof b !== 'string') return;
    }
    if (data.type === 'host:restore' && typeof (data as { state?: unknown }).state !== 'object') return;
    this.onMessage(data as BridgeInbound);
  }

  send(m: BridgeOutbound): void {
    if (!this.embedded) return;
    const target = this.allowed.includes('*') || this.allowed.length === 0 ? '*' : this.allowed[0];
    try {
      window.parent.postMessage(m, target);
    } catch {
      /* host unreachable */
    }
  }

  destroy(): void {
    if (this.embedded) window.removeEventListener('message', this.handler);
  }
}
